"use client";

import { useState, useSyncExternalStore } from "react";
import dynamic from "next/dynamic";
import Link from "next/link";
import Button from "@ishvaram/ui/Button";
import { useAuth } from "@/components/AuthProvider";
import { getAccountSessionSnapshot, subscribeAccountSession } from "@/lib/account-session";
import { resolveCustomerJourneyIdentity } from "@/lib/customer-journey-identity";
import { getLastReadingSnapshot, subscribeLastReading } from "@/lib/last-reading";
import type { User } from "@supabase/supabase-js";

const LoginModal = dynamic(() => import("./LoginModal"), { ssr: false });

interface HeaderAuthProps {
  className?: string;
  fullWidth?: boolean;
}

function userDisplayName(user: User): string | undefined {
  if (typeof user.user_metadata?.full_name === "string") return user.user_metadata.full_name;
  if (typeof user.user_metadata?.name === "string") return user.user_metadata.name;
  return user.email || user.phone || undefined;
}

export default function HeaderAuth({ className = "", fullWidth = false }: HeaderAuthProps) {
  const [showLogin, setShowLogin] = useState(false);
  const { user, loading, signOut } = useAuth();
  const accountSession = useSyncExternalStore(
    subscribeAccountSession,
    getAccountSessionSnapshot,
    () => null
  );
  const rememberedReading = useSyncExternalStore(
    subscribeLastReading,
    getLastReadingSnapshot,
    () => null
  );
  const journey = resolveCustomerJourneyIdentity({
    accountSession,
    authSubject: user,
    rememberedReading,
  });

  if (loading) return <AuthLoadingSlot className={className} fullWidth={fullWidth} />;
  if (user) {
    return (
      <SignedInAuthMenu
        className={className}
        displayName={userDisplayName(user)}
        fullWidth={fullWidth}
        journeyHref={journey.primaryHref}
        onSignOut={signOut}
      />
    );
  }
  if (accountSession || rememberedReading) {
    return (
      <Link
        href={journey.primaryHref}
        prefetch={false}
        className={`inline-flex min-h-10 min-w-[104px] items-center justify-center rounded-lg border px-3 py-2 text-[13px] font-semibold ${
          fullWidth ? "w-full" : ""
        } ${className}`}
        style={{ color: "var(--ish-text)", borderColor: "var(--ish-border)" }}
      >
        {journey.primaryLabel}
      </Link>
    );
  }
  return (
    <>
      <Button
        variant="secondary"
        title="Sign in"
        onPress={() => setShowLogin(true)}
        className={`!min-w-[104px] !py-2 !px-4 !text-[13px] !min-h-[40px] ${
          fullWidth ? "!w-full" : ""
        } ${className}`}
      />
      {showLogin && <LoginModal isOpen={showLogin} onClose={() => setShowLogin(false)} />}
    </>
  );
}

function AuthLoadingSlot({ className, fullWidth }: Required<HeaderAuthProps>) {
  return (
    <span
      aria-hidden
      className={`flex h-10 rounded-lg border ${fullWidth ? "w-full" : "w-[104px]"} ${className}`}
      style={{ borderColor: "var(--ish-border)" }}
    />
  );
}

function SignedInAuthMenu({
  className,
  displayName,
  fullWidth,
  journeyHref,
  onSignOut,
}: {
  className: string;
  displayName?: string;
  fullWidth: boolean;
  journeyHref: string;
  onSignOut: () => Promise<void>;
}) {
  return (
    <details className={`relative ${fullWidth ? "w-full" : ""} ${className}`}>
      <summary
        className={`list-none inline-flex min-h-10 min-w-[104px] cursor-pointer items-center justify-center rounded-lg border px-3 py-2 text-[13px] font-semibold transition hover:bg-[var(--ish-accent-light)] ${
          fullWidth ? "w-full" : ""
        }`}
        style={{ color: "var(--ish-text)", borderColor: "var(--ish-border)" }}
      >
        My Jyotishi
      </summary>
      <div
        className={`absolute right-0 top-full z-50 mt-2 rounded-lg border p-2 shadow-xl ${
          fullWidth ? "w-full" : "w-56"
        }`}
        style={{ background: "var(--ish-bg, #fff)", borderColor: "var(--ish-border)" }}
      >
        {displayName && (
          <p className="truncate px-2 pb-2 text-[12px]" style={{ color: "var(--ish-text-muted)" }}>
            {displayName}
          </p>
        )}
        <Link
          href={journeyHref}
          prefetch={false}
          className="block w-full rounded-md px-2 py-2 text-left text-[13px] font-semibold hover:bg-[var(--ish-accent-light)]"
          style={{ color: "var(--ish-text)" }}
        >
          {journeyHref === "/account/" ? "My readings" : "Continue latest reading"}
        </Link>
        {journeyHref !== "/account/" ? (
          <Link
            href="/account/"
            prefetch={false}
            className="block w-full rounded-md px-2 py-2 text-left text-[13px] font-medium hover:bg-[var(--ish-accent-light)]"
            style={{ color: "var(--ish-text)" }}
          >
            All verified readings
          </Link>
        ) : null}
        <button
          type="button"
          onClick={() => void onSignOut()}
          className="w-full rounded-md px-2 py-2 text-left text-[13px] font-medium hover:bg-[var(--ish-accent-light)]"
          style={{ color: "var(--ish-text)" }}
        >
          Sign out
        </button>
      </div>
    </details>
  );
}
